import React, { useState, useCallback, useEffect, lazy } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { isEmpty } from 'lodash';
import { Navbar, Nav } from 'react-bootstrap';
import useWindowSize from '../../hooks/useWindowSize';
import LoginForm from '../LoginForm/index';
import Loading from '../Loading/Loading';
import { loginUser } from '../../actions/users/loginUser';
import { logoutUser } from '../../actions/users/logoutUser';
import {
  isUserAuthenticated as isUserAuthenticatedSelector,
  getUserDetail as getUserDetailSelector,
  userHasAcceptedTerms as userHasAcceptedTermsSelector,
  getUserPermissionsData as getUserPermissionsDataSelector,
  isUserAdmin as isUserAdminSelector,
} from '../../selectors/users';
import { MOBILE_WIDTH_PX } from '../../constants';
import { AUTH, ROUTER_BASE_PATH } from '../../constants/siteConstants';
import { openModalError, openModalDialog } from '../../utils/modals';
import LocalStorageService from '../../services/LocalStorage/LocalStorage.service';
import { shouldShowData } from '../../utils/utils';
import { setTramiteSeleccionado } from '../../actions/capweb/capwebActions';
import HeaderUserDropDown from '../HeaderUserDropDown';
import { useReCaptcha } from '../../contexts/ReCaptchaContext';

const NewMenuDesplegable = lazy(() => import('../MenuDesplegable'));

const SubHeader = () => {
  const dispatch = useDispatch();
  const isUserAuthenticated = useSelector(isUserAuthenticatedSelector);
  const userDetail = useSelector(getUserDetailSelector);
  const userPermissions = useSelector(getUserPermissionsDataSelector);
  const isUserAdmin = useSelector(isUserAdminSelector);
  const userHasAcceptedTerms = useSelector(userHasAcceptedTermsSelector);
  const auth = useSelector((state) => state.auth);

  const navigate = useNavigate();
  const [, setMenuExpanded] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const { width } = useWindowSize();
  const { shouldShowContent, shouldShowDialog, campaignModalLastShown, currentDate } =
    shouldShowData();
  const { getToken } = useReCaptcha();
  const loginFormSubmit = useCallback(
    async (data) => {
      setIsLoading(true);
      let captchaToken = null;

      try {
        captchaToken = await getToken();
      } catch (error) {
        openModalError({
          title: 'Ups',
        });

        setIsLoading(false);
        return;
      }

      const user = {
        email: data.email,
        password: data.password,
      };

      const redirectToCAP = () => {
        return navigate(`/tramitesonline/capweb`);
      };

      const isCapActive = (permissions) => {
        return permissions?.find((permission) => permission.hasOwnProperty('cap'))?.cap.at(0)
          .active;
      };

      if (!isUserAuthenticated) {
        dispatch(loginUser(user)).then(({ payload }) => {
          if (!isEmpty(payload)) {
            setIsLoading(false);
            if (
              shouldShowContent &&
              isCapActive(payload?.authData?.userPermissions?.userPermissions) &&
              (shouldShowDialog || campaignModalLastShown === undefined) &&
              !window.IS_RUNNING_IN_CYPRESS
            ) {
              const children = `
                Actualizá tus datos y mantené la cartilla de socios con la información correcta.

                Seleccioná “Actualizar” para realizarlo en este momento u “Omitir” si ya lo hiciste o para continuar a la web.`;

              openModalDialog({
                title: 'ACTUALIZA TUS DATOS',
                children,
                action: redirectToCAP,
                icon: false,
                buttonLeftTitle: 'ACTUALIZAR',
                buttonRightTitle: 'OMITIR',
              });
              LocalStorageService.set('campaignModalLastShown', currentDate.getTime());
              dispatch(setTramiteSeleccionado('adp'));
            }
            if (!isEmpty(payload) && window.location.pathname.includes('forgot-password')) {
              navigate('/');
            }
          }
        });
      }
    },
    [setIsLoading],
  );

  useEffect(() => {
    if (auth?.error) {
      openModalError({
        title: 'Ups!',
        children: (
          <div className='d-flex flex-column'>
            <p>{AUTH.LOGIN_UNAUTHORIZED}</p>
            <a className='mt-3' href={`${ROUTER_BASE_PATH}/forgot-password`}>
              <small>Olvidaste tu contraseña?</small>
            </a>
          </div>
        ),
        close: () => window.location.reload(),
      });
    }
  }, [auth?.error]);

  const onLogout = () => {
    dispatch(logoutUser());
    navigate(`${ROUTER_BASE_PATH}/`);
  };

  useEffect(() => {
    if (isUserAuthenticated && !userHasAcceptedTerms) {
      navigate(`/terminos-y-condiciones`);
    }
  }, [isUserAuthenticated, userHasAcceptedTerms]);

  return (
    <Navbar collapseOnSelect expand={false} bg='dark' variant='dark'>
      <div className='col-12 mx-auto position-relative'>
        <div className=' col-xl-8 col-11 m-auto d-flex justify-content-between flex-inline align-items-center '>
          <div className='col-4 p-1'>
            <Navbar.Toggle
              aria-controls='responsive-navbar-nav'
              data-test-id='hamburger-button'
              style={{ fontSize: '1em' }}
            />
            {width > MOBILE_WIDTH_PX && (
              <span className='text-white p-4'>
                <strong>PRESTADORES</strong>
              </span>
            )}
          </div>
          <div className=' p-1 d-flex d-inline justify-content-center'>
            {isLoading ? (
              <Loading />
            ) : isUserAuthenticated ? (
              <HeaderUserDropDown userData={userDetail} logoutUser={onLogout} />
            ) : (
              <LoginForm isLogged={isUserAuthenticated} handleLoginFormSubmit={loginFormSubmit} />
            )}
          </div>
        </div>
        <div className='col-12 mx-auto position-relative ' style={{ zIndex: 1000 }}>
          <Navbar.Collapse id='responsive-navbar-nav'>
            <div
              className='col-12 col-md-12 col-xs-12 col-sm-12 col-lg-12 col-xl-12 position-absolute mt-2'
              style={{ backgroundColor: '#535353' }}
            >
              <Nav>
                <NewMenuDesplegable
                  isUserAuthenticated={isUserAuthenticated}
                  setMenuExpanded={setMenuExpanded}
                  userPermissions={userPermissions}
                  isUserAdmin={isUserAdmin}
                />
              </Nav>
            </div>
          </Navbar.Collapse>
        </div>
      </div>
    </Navbar>
  );
};

export default SubHeader;
